// Gestion des commandes administrateur - Lecture depuis l'API backend
import React, { useEffect, useMemo, useState } from 'react';
import BannerConfigForm from './BannerConfigForm';
import Banner from './Banner';

const AdminOrders = () => {
  // 🔹 États locaux
  const [bannerConfig, setBannerConfig] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('bannerConfig')) || {};
    } catch {
      return {};
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem('adminToken') || '');
  const [authorized, setAuthorized] = useState(() => {
    const stored = localStorage.getItem('adminToken');
    return !!stored;
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [analytics, setAnalytics] = useState({ totalViews: 0, todayViews: 0, uniqueSessions: 0 });

  const showBanner = bannerConfig && bannerConfig.text;

  // ✅ Vérifie le token à chaque changement (envoi au serveur)
  useEffect(() => {
    if (!token) {
      setAuthorized(false);
      return;
    }

    console.log('🔑 Vérification token avec serveur:', token.substring(0, 10) + '...');
    
    // Envoyer le token au serveur pour vérification
    const verifyToken = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const response = await fetch(`${API_URL}/api/orders/ping`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (response.ok) {
          console.log('✅ Token accepté par le serveur');
          setAuthorized(true);
          localStorage.setItem('adminToken', token);
        } else {
          console.log('❌ Token rejeté par le serveur');
          setAuthorized(false);
        }
      } catch (err) {
        console.error('Erreur vérification token:', err);
        setAuthorized(false);
      }
    };

    verifyToken();
  }, [token]);

  // � Récupère les commandes depuis l'API
  useEffect(() => {
    if (!authorized) return;
    
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        console.log('📡 Appel API:', API_URL + '/api/orders');
        console.log('🔐 Envoi du token:', token ? `${token.substring(0, 2)}...` : '(vide)');
        
        const response = await fetch(`${API_URL}/api/orders`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        console.log('📡 Réponse du serveur:', response.status, response.statusText);
        
        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('📦 Données reçues:', data?.length, 'commandes');
        setOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('❌ Erreur:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [authorized, token]);

  // 📊 Récupère les analytics
  useEffect(() => {
    if (!authorized) return;

    const fetchAnalytics = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        
        // Récupérer analytics du jour
        const todayResponse = await fetch(`${API_URL}/api/analytics/today`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        // Récupérer analytics générales
        const allResponse = await fetch(`${API_URL}/api/analytics?period=all`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (todayResponse.ok && allResponse.ok) {
          const todayData = await todayResponse.json();
          const allData = await allResponse.json();
          
          setAnalytics({
            totalViews: allData.totalViews || 0,
            todayViews: todayData.totalViews || 0,
            uniqueSessions: allData.uniqueSessions || 0,
            viewsByDay: allData.viewsByDay || {},
          });
        }
      } catch (err) {
        console.error('❌ Erreur récupération analytics:', err);
      }
    };

    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 60000); // Rafraîchir toutes les minutes
    return () => clearInterval(interval);
  }, [authorized, token]);

  // 🔍 Filtrage des résultats
  const filtered = useMemo(() => {
    if (!query.trim()) return orders;
    const q = query.toLowerCase();
    return orders.filter((o) =>
      [o.name, o.email, o.phone, o.pricing, o.message, o.company]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [orders, query]);

  // 📦 Export CSV
  const exportCSV = () => {
    if (!orders.length) return;
    const header = ['id', 'name', 'email', 'phone', 'company', 'pricing', 'message', 'createdAt'];
    const rows = orders.map((o) => header.map((h) => JSON.stringify(o[h] ?? '')));
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'orders.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // 📋 Copier tous les emails
  const copyEmails = async () => {
    const emails = orders.map((o) => o.email).filter(Boolean).join('\n');
    if (!emails) return;
    try {
      await navigator.clipboard.writeText(emails);
      alert('Emails copiés dans le presse-papiers');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = emails;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert('Emails copiés (fallback)');
    }
  };

  // 📊 Statistiques
  const stats = useMemo(() => {
    const total = orders.length;
    const last = orders[0]?.createdAt ? new Date(orders[0].createdAt) : null;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const weekly = orders.filter(
      (o) => o.createdAt && new Date(o.createdAt).getTime() >= weekAgo
    ).length;
    return { total, last, weekly };
  }, [orders]);

  // 🔒 Écran de connexion admin
  if (!authorized) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-[#2f3b52] via-[#3d4e68] to-[#4a5a7d] p-4">
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-2xl border border-white/40 w-full max-w-md p-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-[#f7876f]"><path d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm-3 8V6a3 3 0 116 0v3H9z"/></svg>
            <h2 className="text-2xl font-extrabold text-[#2f3b52]">Accès Admin</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4 text-center">Entrez votre jeton pour voir les commandes.</p>
          <div className="relative mb-3">
            <input
              type={showPassword ? 'text' : 'password'}
              className="w-full pl-4 pr-12 py-3 rounded-xl border-2 border-gray-200 focus:border-[#f7876f] focus:ring-2 focus:ring-[#f7876f]/20 outline-none transition"
              placeholder="Jeton d'accès"
              value={token}
              onChange={(e) => setToken(e.target.value.trim())}
              onKeyDown={(e) => { if (e.key === 'Enter') setToken(token); }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          <button
            onClick={() => setToken(token)}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white font-semibold shadow hover:from-[#e97058] hover:to-[#d96049]"
          >
            Valider
          </button>
          {token && !authorized && (
            <p className="text-red-600 text-sm mt-3 text-center">Jeton invalide.</p>
          )}
        </div>
      </div>
    );
  }

  // ✅ Vue principale
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* En-tête Premium */}
        <div className="bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] rounded-2xl p-8 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black mb-2">Dashboard Commandes</h1>
              <p className="text-blue-100">Gérez et suivez toutes vos demandes de service</p>
            </div>
            <span className="relative inline-flex items-center">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400"></span>
              </span>
              <span className="ml-3 text-sm font-semibold text-green-200 bg-green-900/30 px-3 py-1 rounded-full border border-green-400/50">Système actif</span>
            </span>
          </div>
        </div>

        {/* Statistiques Premium */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-[#f7876f]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-1">Total Commandes</p>
                <p className="text-4xl font-black text-[#2f3b52]">{stats.total}</p>
              </div>
              <div className="bg-gradient-to-br from-[#f7876f]/20 to-[#f7876f]/5 p-4 rounded-xl">
                <span className="text-3xl">📋</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-1">Cette Semaine</p>
                <p className="text-4xl font-black text-blue-600">{stats.weekly}</p>
              </div>
              <div className="bg-blue-100/50 p-4 rounded-xl">
                <span className="text-3xl">📈</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-1">Vues du Site</p>
                <p className="text-4xl font-black text-green-600">{analytics.totalViews}</p>
              </div>
              <div className="bg-green-100/50 p-4 rounded-xl">
                <span className="text-3xl">👁️</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-1">Vues Aujourd'hui</p>
                <p className="text-4xl font-black text-purple-600">{analytics.todayViews}</p>
              </div>
              <div className="bg-purple-100/50 p-4 rounded-xl">
                <span className="text-3xl">📊</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold mb-1">Sessions Uniques</p>
                <p className="text-4xl font-black text-orange-600">{analytics.uniqueSessions}</p>
              </div>
              <div className="bg-orange-100/50 p-4 rounded-xl">
                <span className="text-3xl">👥</span>
              </div>
            </div>
          </div>
        </div>

        {/* Outils de Gestion */}
        <div className="bg-white rounded-2xl p-6 shadow-md">
          <div className="flex flex-col md:flex-row gap-4 items-center md:items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Recherche avancée</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Recherchez par nom, email, type de service..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#f7876f] transition-colors"
                />
                <span className="absolute left-3 top-3.5 text-gray-400">🔍</span>
              </div>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button 
                onClick={copyEmails} 
                disabled={!orders.length}
                title="Copie tous les emails des commandes"
                className="flex-1 md:flex-none px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                📧 Copier Emails
              </button>
              <button 
                onClick={exportCSV} 
                disabled={!orders.length}
                title="Exporte les données en CSV"
                className="flex-1 md:flex-none px-4 py-3 bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] hover:from-[#3d4e68] hover:to-[#4a5a78] text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                📊 Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Bannière config */}
        <div className="space-y-4">
          <BannerConfigForm onSave={setBannerConfig} />
          {showBanner && (
            <Banner text={bannerConfig.text} color={bannerConfig.color} endDate={bannerConfig.endDate} />
          )}
        </div>

        {/* État du chargement */}
        {loading && (
          <div className="bg-white rounded-2xl p-12 shadow-md text-center">
            <div className="inline-block mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#f7876f]"></div>
            </div>
            <p className="text-gray-500 font-semibold">Chargement des commandes...</p>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 shadow-md">
            <p className="text-red-700 font-semibold">⚠️ Erreur: {error}</p>
          </div>
        )}

        {/* Aucune commande */}
        {!loading && !error && filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-12 shadow-md text-center">
            <p className="text-gray-400 text-lg font-semibold">📭 Aucune commande trouvée</p>
            <p className="text-gray-500 text-sm mt-2">Vos demandes apparaîtront ici</p>
          </div>
        )}

        {/* Tableau des commandes */}
        {!loading && !error && filtered.length > 0 && (
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            {/* En-tête du tableau */}
            <div className="bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] p-6">
              <h3 className="text-white font-bold text-lg">📋 {filtered.length} Commande{filtered.length > 1 ? 's' : ''}</h3>
            </div>

            {/* Tableau responsive */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Nom</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Entreprise</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((o) => {
                    const dateStr = o.createdAt ? new Date(o.createdAt).toLocaleDateString('fr-FR') : '—';
                    return (
                      <tr key={o.id} className="hover:bg-blue-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-700">{dateStr}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-[#2f3b52]">{o.name}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            {o.email && (
                              <a href={`mailto:${o.email}`} className="text-[#f7876f] hover:underline font-medium block">
                                {o.email}
                              </a>
                            )}
                            {o.phone && (
                              <a href={`tel:${o.phone}`} className="text-gray-600 hover:text-gray-900 text-xs mt-1">
                                {o.phone}
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-[#f7876f]/20 to-[#f7876f]/10 text-[#f7876f] border border-[#f7876f]/30">
                            {o.pricing}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {o.company || '—'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => setSelectedOrder(o)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#f7876f] to-[#e97058] hover:from-[#e97058] hover:to-[#d96049] text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all duration-300"
                          >
                            👁️ Voir détails
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal Détails Complets */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header Premium */}
            <div className="bg-gradient-to-r from-[#2f3b52] via-[#3d4e68] to-[#f7876f] text-white p-8 flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-black">📋 Commande</h3>
                <p className="text-blue-100 text-sm mt-2 font-medium">
                  ID: {selectedOrder.id.substring(0, 12)}...
                </p>
                <p className="text-blue-100 text-xs mt-1">
                  📅 {selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleString('fr-FR') : '—'}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-white hover:bg-white/20 p-3 rounded-xl transition-all duration-300 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Content avec scroll */}
            <div className="overflow-y-auto flex-1 p-8 space-y-6">
              {/* Section Client - Cartes premium */}
              <div>
                <h4 className="text-xl font-black text-[#2f3b52] mb-4 flex items-center gap-2">
                  👤 Informations Client
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-5 rounded-2xl border border-blue-200/50">
                    <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">Nom</p>
                    <p className="text-lg font-bold text-[#2f3b52] mt-2">{selectedOrder.name}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 p-5 rounded-2xl border border-orange-200/50">
                    <p className="text-xs text-orange-600 font-bold uppercase tracking-wider">Email</p>
                    <a href={`mailto:${selectedOrder.email}`} className="text-lg font-bold text-[#f7876f] hover:underline mt-2 break-all">
                      {selectedOrder.email}
                    </a>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-5 rounded-2xl border border-green-200/50">
                    <p className="text-xs text-green-600 font-bold uppercase tracking-wider">Téléphone</p>
                    <a href={`tel:${selectedOrder.phone}`} className="text-lg font-bold text-green-700 hover:underline mt-2">
                      {selectedOrder.phone || '—'}
                    </a>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 p-5 rounded-2xl border border-purple-200/50">
                    <p className="text-xs text-purple-600 font-bold uppercase tracking-wider">Entreprise</p>
                    <p className="text-lg font-bold text-purple-700 mt-2">{selectedOrder.company || 'Non renseignée'}</p>
                  </div>
                </div>
              </div>

              {/* Section Commande */}
              <div>
                <h4 className="text-xl font-black text-[#2f3b52] mb-4 flex items-center gap-2">
                  🛍️ Service Commandé
                </h4>
                <div className="bg-gradient-to-r from-[#f7876f]/10 to-[#f7876f]/5 p-6 rounded-2xl border-2 border-[#f7876f]/30">
                  <p className="text-xs text-[#f7876f] font-bold uppercase tracking-wider mb-2">Type de Solution</p>
                  <p className="text-2xl font-black text-[#2f3b52]">{selectedOrder.pricing}</p>
                </div>
              </div>

              {/* Section Description */}
              <div>
                <h4 className="text-xl font-black text-[#2f3b52] mb-4 flex items-center gap-2">
                  💬 Description du Projet
                </h4>
                <div className="bg-gray-50 p-6 rounded-2xl border-2 border-gray-200">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed font-medium">
                    {selectedOrder.message || '—'}
                  </p>
                </div>
              </div>

              {/* Section Champs Dynamiques */}
              {Object.keys(selectedOrder).filter(key => 
                !['id', 'name', 'email', 'phone', 'company', 'pricing', 'message', 'createdAt', 'description'].includes(key)
              ).length > 0 && (
                <div>
                  <h4 className="text-xl font-black text-[#2f3b52] mb-4 flex items-center gap-2">
                    ✨ Détails Spécifiques
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    {Object.keys(selectedOrder)
                      .filter(key => 
                        !['id', 'name', 'email', 'phone', 'company', 'pricing', 'message', 'createdAt', 'description'].includes(key)
                      )
                      .map((key, idx) => {
                        // Humanizer le nom de la clé
                        const humanizedKey = key
                          .charAt(0).toUpperCase() + 
                          key.slice(1).replace(/([A-Z])/g, ' $1');
                        
                        const colors = [
                          'from-indigo-50 to-indigo-100/50 border-indigo-200/50 text-indigo-600',
                          'from-cyan-50 to-cyan-100/50 border-cyan-200/50 text-cyan-600',
                          'from-emerald-50 to-emerald-100/50 border-emerald-200/50 text-emerald-600',
                          'from-rose-50 to-rose-100/50 border-rose-200/50 text-rose-600',
                        ];
                        const colorClass = colors[idx % colors.length];
                        
                        return (
                          <div key={key} className={`bg-gradient-to-br ${colorClass} p-5 rounded-2xl border-2`}>
                            <p className={`text-xs font-bold uppercase tracking-wider text-${colorClass.split(' ')[0].replace('from-', '').replace('-50', '')}-600`}>
                              {humanizedKey}
                            </p>
                            <p className="text-base font-semibold text-gray-800 mt-2 whitespace-pre-wrap">
                              {selectedOrder[key]}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer avec actions */}
            <div className="bg-gray-100 p-6 border-t flex gap-3 flex-wrap">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(selectedOrder.email);
                    alert('✅ Email copié au presse-papiers!');
                  } catch {
                    alert('❌ Erreur lors de la copie');
                  }
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-300 text-sm"
              >
                📧 Copier Email
              </button>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(selectedOrder.id);
                    alert('✅ ID copié au presse-papiers!');
                  } catch {
                    alert('❌ Erreur lors de la copie');
                  }
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-300 text-sm"
              >
                🔗 Copier ID
              </button>
              <button
                onClick={() => setSelectedOrder(null)}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#2f3b52] to-[#3d4e68] hover:from-[#3d4e68] hover:to-[#4a5a78] text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-300 text-sm"
              >
                ✕ Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
